import { QuoteLine, type QuoteLineList } from '@kalos/kalos-rpc';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { QuoteLineClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export type QuoteLineFilters = { [K in keyof QuoteLine]?: QuoteLine[K] };

const quoteLineQueryKey = (filters: QuoteLineFilters) => [
  queryKeys.quoteLine.root,
  queryKeys.quoteLine.list,
  queryKeys.quoteLine.getQuoteLineHash(filters),
  filters.quoteDocumentId ?? null,
];

export const useQuoteLineQuery = <TSelectData = QuoteLineList,>({
  enabled = true,
  filters = {},
  select,
}: {
  enabled?: boolean;
  filters?: QuoteLineFilters;
  select?: (data: QuoteLineList) => TSelectData;
} = {}) => {
  return useQuery({
    queryFn: () => {
      const request = QuoteLine.create(filters);
      return QuoteLineClientService.BatchGet(request);
    },
    queryKey: quoteLineQueryKey(filters),
    enabled,
    select,
    refetchOnWindowFocus: false,
  });
};

const FLAT_RATE_CATALOG_FILTERS: QuoteLineFilters = {
  isActive: 1,
  isFlatrate: '1',
  pageNumber: 0,
  withoutLimit: true,
};

/**
 * The flat rate book: catalog entries only, so rows already attached to a job are dropped.
 * Cached for the session because consumers filter it client-side as the user types.
 * `useCreateQuoteLineMutation` writes into every quoteLine cache entry with a null
 * quoteDocumentId, so the isFlatrate guard keeps freshly added job lines out of the catalog.
 */
export const useFlatRateCatalogQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryFn: () => QuoteLineClientService.BatchGet(QuoteLine.create(FLAT_RATE_CATALOG_FILTERS)),
    queryKey: quoteLineQueryKey(FLAT_RATE_CATALOG_FILTERS),
    enabled,
    select: (data: QuoteLineList) =>
      data.results
        .filter(
          (line) => line.isFlatrate === '1' && (!line.jobNumber || line.jobNumber.trim() === ''),
        )
        .sort((a, b) => a.description.localeCompare(b.description)),
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetches lines per quote document by quoteDocumentId and flattens them. A line is tied to a
 * proposal by quoteDocumentId, not by jobNumber, so fetching by jobNumber misses lines whose
 * jobNumber doesn't match the event (e.g. follow-up jobs) and undercounts per-card totals.
 * Inactive lines are kept so declined proposals still reflect their original proposed total.
 */
export const useQuoteLinesByDocumentIds = (documentIds: number[]) => {
  const ids = useMemo(() => [...new Set(documentIds.filter(Boolean))], [documentIds]);
  const queryResults = useQueries({
    queries: ids.map((quoteDocumentId) => {
      const filters: QuoteLineFilters = { quoteDocumentId, withoutLimit: true };
      return {
        queryFn: () => QuoteLineClientService.BatchGet(QuoteLine.create(filters)),
        queryKey: quoteLineQueryKey(filters),
        refetchOnWindowFocus: false,
      };
    }),
  });
  return useMemo(
    () => ({
      results: queryResults.flatMap((q) => q.data?.results ?? []),
      isLoading: queryResults.some((q) => q.isLoading),
    }),
    [queryResults],
  );
};

export const useCreateQuoteLineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quoteLine: QuoteLine) => {
      return QuoteLineClientService.Create(quoteLine);
    },
    onSuccess: (createdQuoteLine) => {
      if (createdQuoteLine) {
        queryClient.setQueriesData<QuoteLineList>(
          {
            predicate: (query) =>
              query.queryKey[0] === queryKeys.quoteLine.root &&
              (query.queryKey[3] == null || query.queryKey[3] === createdQuoteLine.quoteDocumentId),
          },
          (cache) => {
            if (cache) {
              // Only add it if it's not already in the results (avoid duplicates)
              const alreadyExists = cache.results.some((line) => line.id === createdQuoteLine.id);
              if (alreadyExists) {
                return cache;
              }

              return {
                ...cache,
                results: [...cache.results, createdQuoteLine],
                totalCount: cache.totalCount + 1,
              };
            }
            return cache;
          },
        );
      }
    },
  });
};

export const useUpdateQuoteLineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quoteLine: QuoteLine) => {
      return QuoteLineClientService.Update(quoteLine);
    },
    onSuccess: (updatedQuoteLine) => {
      if (updatedQuoteLine) {
        // Update all matching query cache entries with the updated item
        queryClient.setQueriesData<QuoteLineList>(
          { predicate: (query) => query.queryKey[0] === queryKeys.quoteLine.root },
          (cache) => {
            if (cache) {
              const results = cache.results.map((line) => {
                if (line.id === updatedQuoteLine.id) {
                  return updatedQuoteLine;
                }
                return line;
              });

              return {
                ...cache,
                results,
              };
            }
            return cache;
          },
        );
      }
    },
  });
};

export const useDeleteQuoteLineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quoteLine: QuoteLine) => {
      return QuoteLineClientService.Delete(quoteLine);
    },
    onSuccess: (_, deletedQuoteLine) => {
      // Update all matching query cache entries with the deleted item
      queryClient.setQueriesData<QuoteLineList>(
        { predicate: (query) => query.queryKey[0] === queryKeys.quoteLine.root },
        (cache) => {
          if (cache) {
            return {
              ...cache,
              results: cache.results.filter((line) => line.id !== deletedQuoteLine.id),
              totalCount: Math.max(0, cache.totalCount - 1), // Adjust the count
            };
          }
          return cache;
        },
      );
    },
  });
};
